/*
 * /setup/user (page)
 */
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { supabase } from "@psychology/auth/client"
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Container,
	Form,
	FormSubmitButton,
	InputFormField,
	Page,
	PageHeader,
	SelectFormField,
	TextareaFormField,
} from "@psychology/react-ui"
import { ArrowLeft } from "lucide-react"
import { toast } from "sonner"
import { z } from "zod"
import { trpc } from "../../trpc"

export const Route = createFileRoute("/setup/profile")({
	component: SetupAccountPage,
	loader: async () => {
		const { data, error } = await supabase.auth.getSession()
		if (error) {
			console.error("Auth getSession Error", { error })
		}

		if (!data.session) {
			throw redirect({ to: "/auth" })
		}

		return { user: data.session.user }
	},
})

const userSetupSchema = z.object({
	// preferredName: z.string().optional(),
	birthYear: z.coerce.number().min(1900).max(2025).optional().or(z.literal("")),
	maritalStatus: z.string().optional(),
	occupation: z.string().optional(),
	education: z.string().optional(),
	housing: z.string().optional(),
	finances: z.string().optional(),
	religion: z.string().optional(),
	keyRoutines: z.string().optional(),
	additionalNotes: z.string().optional(),
})

type AccountSetupFormData = z.infer<typeof userSetupSchema>

const maritalStatusOptions = [
	{ label: "Prefer not to say", value: "-" },
	{ label: "Single", value: "single" },
	{ label: "In a relationship", value: "in_a_relationship" },
	{ label: "Married", value: "married" },
	{ label: "Divorced", value: "divorced" },
	{ label: "Widowed", value: "widowed" },
	{ label: "Other", value: "other" },
]

function SetupAccountPage() {
	const { user } = Route.useLoaderData()
	const navigate = useNavigate()

	const createMutation = trpc.user.create.useMutation()
	const updateMutation = trpc.user.update.useMutation()

	const { data: existingAccount, isLoading: userLoading } =
		trpc.user.getByFilter.useQuery(
			{ where: { userId: user?.id ?? "" } },
			{ enabled: !!user },
		)

	async function handleSubmit(data: AccountSetupFormData) {
		if (!user) {
			return "You must be logged in to save your user"
		}

		try {
			const userData = {
				userId: user.id,
				// preferredName:
				// 	data.preferredName || user.email?.split("@")[0] || "User",
				birthYear: data.birthYear ? Number(data.birthYear) : undefined,
				maritalStatus: data.maritalStatus || undefined,
				occupation: data.occupation || undefined,
				education: data.education || undefined,
				housing: data.housing || undefined,
				finances: data.finances || undefined,
				religion: data.religion || undefined,
				keyRoutines: data.keyRoutines || undefined,
				additionalNotes: data.additionalNotes || undefined,
			} as any

			if (existingAccount) {
				await updateMutation.mutateAsync(userData)
			} else {
				await createMutation.mutateAsync(userData)
			}

			toast.success("Account setup complete!")
			navigate({ to: "/dashboard" })
		} catch (error) {
			const err = error as Error
			toast.error(err.message || "Failed to save user")
			return err.message || "Failed to save user"
		}
	}

	function handleSkip() {
		navigate({ to: "/dashboard" })
	}

	if (userLoading) {
		return (
			<Page centerVertical={true}>
				<Container width={"lg"} padding={"lg"}>
					<div className={"text-center"}>Loading...</div>
				</Container>
			</Page>
		)
	}

	if (!user) {
		navigate({ to: "/auth" })
		return null
	}

	const defaultValues: AccountSetupFormData = {
		// preferredName: existingAccount?.preferredName || undefined,
		birthYear: existingAccount?.birthYear || undefined,
		maritalStatus: existingAccount?.maritalStatus || undefined,
		occupation: existingAccount?.occupation || undefined,
		education: existingAccount?.education || undefined,
		housing: existingAccount?.housing || undefined,
		finances: existingAccount?.finances || undefined,
		religion: existingAccount?.religion || undefined,
		keyRoutines: existingAccount?.keyRoutines || undefined,
		additionalNotes: existingAccount?.additionalNotes || undefined,
	}

	return (
		<Page className={"p-2 sm:p-4 md:p-6 lg:p-8"} centerVertical={false}>
			<Container width={"lg"} padding={"lg"} gap={"sm"}>
				<PageHeader
					title={"Account Setup"}
					left={{
						href: "/",
						label: "Home",
						Icon: <ArrowLeft size={20} />,
					}}
				/>

				<Card>
					<CardHeader>
						<CardTitle>{"Tell us about yourself"}</CardTitle>
						<CardDescription>
							{
								"All fields are optional. You can update this information anytime from your user settings."
							}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Form<AccountSetupFormData>
							schema={userSetupSchema}
							defaultValues={defaultValues}
							onSubmit={handleSubmit}
							resetOnSuccess={false}
							className={"gap-6"}
						>
							<div className={"grid gap-4 md:grid-cols-2"}>
								<InputFormField
									name={"preferredName"}
									label={"Preferred Name"}
									placeholder={"What should we call you?"}
								/>
								<InputFormField
									name={"birthYear"}
									label={"Birth Year"}
									type={"number"}
									placeholder={"e.g., 1990"}
								/>
							</div>

							<div className={"grid gap-4 md:grid-cols-2"}>
								<SelectFormField
									name={"maritalStatus"}
									label={"Relationship Status"}
									placeholder={"Select..."}
									options={maritalStatusOptions}
								/>
								<InputFormField
									name={"occupation"}
									label={"Occupation"}
									placeholder={"What do you do?"}
								/>
							</div>

							<div className={"grid gap-4 md:grid-cols-2"}>
								<InputFormField
									name={"education"}
									label={"Education"}
									placeholder={"Highest level of education"}
								/>
								<InputFormField
									name={"housing"}
									label={"Housing Situation"}
									placeholder={"e.g., Apartment, House"}
								/>
							</div>

							<div className={"grid gap-4 md:grid-cols-2"}>
								<InputFormField
									name={"finances"}
									label={"Financial Situation"}
									placeholder={"General description"}
								/>
								<InputFormField
									name={"religion"}
									label={"Religion/Spirituality"}
									placeholder={"Optional"}
								/>
							</div>

							<TextareaFormField
								name={"keyRoutines"}
								label={"Key Routines"}
								placeholder={"Describe any important daily routines or habits"}
								rows={3}
							/>

							<TextareaFormField
								name={"additionalNotes"}
								label={"Additional Notes"}
								placeholder={"Anything else you'd like us to know?"}
								rows={3}
							/>

							<div className={"flex justify-between pt-4"}>
								<Button type={"button"} variant={"ghost"} onClick={handleSkip}>
									{"Skip for now"}
								</Button>
								<FormSubmitButton>{"Save & Continue"}</FormSubmitButton>
							</div>
						</Form>
					</CardContent>
				</Card>
			</Container>
		</Page>
	)
}
